import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FormSection } from "@/components/entity-page/FormSection";
import { createFormOption } from "@/lib/api";

const metadataSchema = z.string().refine((val) => {
  if (!val) return true;
  try {
    JSON.parse(val);
    return true;
  } catch {
    return false;
  }
}, "Metadata must be valid JSON");

export default function CreateProviderForm() {
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      value: "",
      label: "",
      description: "",
      order: 0,
      metadata: "",
    },
    onSubmit: async ({ value }) => {
      try {
        const result = await createFormOption("providers", value.value, value.label, value.order, {
          description: value.description || undefined,
          metadata: value.metadata || undefined,
        });

        if (result.success) {
          toast.success("Provider created successfully");
          navigate({ to: "/dashboard/settings/providers" });
        } else {
          toast.error("Failed to create provider", {
            description: result.error,
          });
        }
      } catch (error) {
        console.error("Error creating provider:", error);
        toast.error("An error occurred", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="space-y-6"
    >
      <FormSection
        title="Provider Details"
        description="A provider is what Agent.providerId references - it determines which telephony/voice provider and TwiML behavior an agent uses."
      >
        <form.Field
          name="value"
          validators={{
            onChange: z.string().min(1, "Value is required"),
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="value" className="text-foreground">
                Value
              </Label>
              <Input
                id="value"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                Stable identifier used internally as the agent's providerId. Cannot be changed later.
              </p>
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="label"
          validators={{
            onChange: z.string().min(1, "Label is required"),
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="label" className="text-foreground">
                Label
              </Label>
              <Input
                id="label"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
              />
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="description">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground">
                Description
              </Label>
              <Textarea
                id="description"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className="text-foreground"
                placeholder="What this provider is for"
              />
            </div>
          )}
        </form.Field>

        <form.Field name="order">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="order" className="text-foreground">
                Display Order
              </Label>
              <Input
                id="order"
                type="number"
                value={field.state.value}
                onChange={(e) => field.handleChange(Number(e.target.value))}
                onBlur={field.handleBlur}
                className="text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                Controls the display order of this option in select dropdowns elsewhere in the app (e.g. the agent
                form). Lower numbers appear first.
              </p>
            </div>
          )}
        </form.Field>

        <form.Field
          name="metadata"
          validators={{
            onChange: metadataSchema,
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="metadata" className="text-foreground">
                Metadata (JSON)
              </Label>
              <Textarea
                id="metadata"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder='{"twimlNoun": "ConversationRelay", "providerConfig": { ... }}'
                className="font-mono text-sm"
                rows={6}
              />
              <p className="text-xs text-muted-foreground">
                Optional JSON object for provider-specific configuration, e.g. twimlNoun (which Twilio TwiML noun
                bridges a call for agents using this provider) and providerConfig.
              </p>
              {field.state.meta.errors && field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message || "Invalid value"}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </FormSection>

      <div className="flex justify-end">
        <Button type="submit" disabled={form.state.isSubmitting}>
          Create Provider
        </Button>
      </div>
    </form>
  );
}
